import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CssBaseline,
  Divider,
  IconButton,
  Input,
  Link,
  Sheet,
  Snackbar,
  Stack,
  ThemeProvider,
  Tooltip,
  Typography,
} from '@mui/joy';
import HomeIcon from '@mui/icons-material/Home';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import LaunchIcon from '@mui/icons-material/Launch';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { theme } from '../../main/theme/constants';
import { scanResolve } from '../../main/API/shipmentPiece';
import { translateAxiosErrorMessage } from '../../main/utils/masterApiError';

const KIND_ICON = {
  piece: Inventory2Icon,
  shipment: LocalShippingIcon,
  unit: InventoryIcon,
};

const KIND_LABEL = {
  piece: 'universalScanner.kindPiece',
  shipment: 'universalScanner.kindShipment',
  unit: 'universalScanner.kindUnit',
};

export default function UniversalScannerScreen() {
  const { t } = useTranslation('ui');
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const [barcode, setBarcode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]); // newest first
  const [snack, setSnack] = useState({ open: false, message: '', color: 'success' });

  const openTarget = useCallback(
    (entry) => {
      if (!entry || !entry.kind) return;
      if (entry.kind === 'piece' && entry.piece && entry.piece.id) {
        // Open shipment edit screen for context plus barcode for the piece
        if (entry.piece.shipment) {
          window.open(`/Shipment/${entry.piece.shipment}/EditShipment`, '_blank', 'noopener');
        }
        return;
      }
      if (entry.kind === 'shipment' && entry.shipment && entry.shipment.id) {
        navigate(`/Shipment/${entry.shipment.id}/EditShipment`);
        return;
      }
      if (entry.kind === 'unit' && entry.unit && entry.unit.id) {
        navigate('/shipment/units');
      }
    },
    [navigate]
  );

  const handleSubmit = useCallback(async () => {
    const code = barcode.trim();
    if (!code) return;
    setBarcode('');
    setSubmitting(true);
    try {
      const res = await scanResolve(code);
      const data = res && res.data;
      const kind = data && data.kind;
      if (!kind) {
        setSnack({ open: true, color: 'danger', message: t('universalScanner.notRecognized') });
      } else {
        const entry = {
          id: `${code}-${Date.now()}`,
          barcode: code,
          kind,
          piece: data.piece || null,
          shipment: data.shipment || null,
          unit: data.unit || null,
        };
        setHistory((prev) => [entry, ...prev]);
      }
    } catch (e) {
      const code404 = e && e.response && e.response.status === 404;
      const msg = code404
        ? t('universalScanner.notRecognized')
        : translateAxiosErrorMessage(e, t) || t('universalScanner.notRecognized');
      setSnack({ open: true, color: 'danger', message: msg });
    } finally {
      setSubmitting(false);
      if (inputRef.current) inputRef.current.focus();
    }
  }, [barcode, t]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', bgcolor: '#FAFAFA' }}>
        <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto' }}>
          <Breadcrumbs sx={{ mb: 1 }}>
            <Link onClick={() => navigate('/home')} sx={{ cursor: 'pointer' }} underline="hover">
              <HomeIcon sx={{ fontSize: 16, mr: 0.5 }} />
              {t('nav.home')}
            </Link>
            <Typography>{t('nav.universalScanner')}</Typography>
          </Breadcrumbs>

          <Box sx={{ mb: 3 }}>
            <Typography level="h3" sx={{ fontWeight: 700 }}>
              {t('universalScanner.pageTitle')}
            </Typography>
            <Typography level="body-sm" sx={{ color: '#6B7280' }}>
              {t('universalScanner.subtitle')}
            </Typography>
          </Box>

          <Sheet variant="outlined" sx={{ p: 2, borderRadius: '12px', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <QrCodeScannerIcon sx={{ color: '#5f09c2' }} />
              <Typography level="title-md" sx={{ flex: 1 }}>
                {t('universalScanner.scanInput')}
              </Typography>
            </Box>
            <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} sx={{ display: 'flex', gap: 1 }}>
              <Input
                slotProps={{ input: { ref: inputRef, autoFocus: true } }}
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                placeholder={t('universalScanner.scanInput')}
                disabled={submitting}
                sx={{ flex: 1, fontFamily: 'monospace' }}
              />
              <Button type="submit" disabled={submitting || !barcode.trim()}>
                {t('scanPiece.submit')}
              </Button>
            </Box>
          </Sheet>

          <Sheet variant="outlined" sx={{ p: 2, borderRadius: '12px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
              <Typography level="title-md" sx={{ flex: 1 }}>
                {t('universalScanner.history')}
              </Typography>
              <IconButton
                size="sm"
                variant="plain"
                color="neutral"
                onClick={() => setHistory([])}
                disabled={history.length === 0}
                title={t('universalScanner.clearHistory')}
              >
                <DeleteSweepIcon />
              </IconButton>
            </Box>
            <Divider sx={{ mb: 1 }} />
            {history.length === 0 ? (
              <Typography level="body-sm" sx={{ color: 'text.tertiary', py: 3, textAlign: 'center' }}>
                {t('scanPiece.scanListEmpty')}
              </Typography>
            ) : (
              <Stack spacing={1}>
                {history.map((entry) => {
                  const IconCmp = KIND_ICON[entry.kind] || QrCodeScannerIcon;
                  const labelKey = KIND_LABEL[entry.kind];
                  const titleParts = [];
                  if (entry.kind === 'piece' && entry.piece) {
                    titleParts.push(entry.piece.human_label || `#${entry.piece.id}`);
                  } else if (entry.kind === 'shipment' && entry.shipment) {
                    titleParts.push(entry.shipment.tracking_number || `#${entry.shipment.id}`);
                  } else if (entry.kind === 'unit' && entry.unit) {
                    titleParts.push(entry.unit.serial_number || `#${entry.unit.id}`);
                  }
                  return (
                    <Sheet
                      key={entry.id}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        bgcolor: 'background.surface',
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '10px',
                          bgcolor: 'primary.softBg',
                          color: 'primary.solidBg',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <IconCmp />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {labelKey && (
                            <Chip size="sm" variant="soft" color="primary">
                              {t(labelKey)}
                            </Chip>
                          )}
                          <Typography level="title-sm" sx={{ flex: 1 }} noWrap>
                            {titleParts.join(' · ') || entry.barcode}
                          </Typography>
                        </Stack>
                        <Typography level="body-xs" sx={{ fontFamily: 'monospace', color: 'text.tertiary' }}>
                          {entry.barcode}
                        </Typography>
                      </Box>
                      <Tooltip title={t('universalScanner.open')}>
                        <IconButton variant="plain" color="primary" onClick={() => openTarget(entry)}>
                          <LaunchIcon />
                        </IconButton>
                      </Tooltip>
                    </Sheet>
                  );
                })}
              </Stack>
            )}
          </Sheet>
        </Box>
      </Box>

      <Snackbar
        autoHideDuration={3500}
        open={snack.open}
        variant="soft"
        color={snack.color}
        onClose={(_, reason) => { if (reason !== 'clickaway') setSnack((s) => ({ ...s, open: false })); }}
      >
        {snack.message}
      </Snackbar>
    </ThemeProvider>
  );
}
